/** The two fields of a CRM tag a key is derived from (structurally a KeapTag). */
type KeapTag = { externalId: string; name: string };

export const STAGES = ['Prospect', 'Lead', 'Existing Customer'] as const;
export const SOURCE_KEY = 'Captured Card';
/** Keys the server reserves for the core vocabulary; a generic tag may not take one. */
const CORE_KEYS = new Set([...STAGES, SOURCE_KEY].map((k) => k.toLowerCase()));
const KEY_RE = /^[A-Za-z0-9][A-Za-z0-9 _-]{0,63}$/;

/**
 * A CRM tag name as an internal key.
 *
 * The key must match the server's key pattern, must not collide with a core key, and must
 * not overwrite a different tag that happens to sanitise to the same thing ("VIP!" and
 * "VIP?"). A tag added before keeps its key, so re-adding it restores rather than forks.
 */
export function keyForTag(
  tag: KeapTag,
  existing: ReadonlyArray<{ canonicalKey: string; externalTagId: string | null }>,
): string {
  const same = existing.find((e) => e.externalTagId === tag.externalId && KEY_RE.test(e.canonicalKey)
    && !CORE_KEYS.has(e.canonicalKey.toLowerCase()));
  if (same) return same.canonicalKey;

  let base = tag.name.replace(/[^A-Za-z0-9 _-]+/g, ' ').replace(/\s+/g, ' ').trim()
    .replace(/^[^A-Za-z0-9]+/, '').slice(0, 64).trim();
  if (!base) base = `Tag ${tag.externalId}`.slice(0, 64);
  if (CORE_KEYS.has(base.toLowerCase())) base = `${base.slice(0, 60)} tag`;
  const taken = new Set(existing.map((e) => e.canonicalKey.toLowerCase()));
  let key = base;
  for (let n = 2; taken.has(key.toLowerCase()) || CORE_KEYS.has(key.toLowerCase()); n += 1) {
    const suffix = ` ${n}`;
    key = `${base.slice(0, 64 - suffix.length).trim()}${suffix}`;
  }
  return key;
}
